"use client"

import { useEffect } from "react"
import { getLogger } from "@/lib/logger"

const log = getLogger('components.error-listeners')

function isAutomationInjectedHydrationMismatch(message: string): boolean {
  return (
    (
      message.includes("Hydration failed because the server rendered HTML didn't match the client") ||
      message.includes("A tree hydrated but some attributes of the server rendered HTML didn't match the client properties")
    ) &&
    message.includes("data-cursor-ref")
  )
}

/**
 * Firefox for iOS (and Firefox Reader Mode) probes for `__firefox__` /
 * `window.__firefox__` on page load. Keep this predicate aligned with the
 * tested predicate in `@aient/otel-browser`'s browser error instrumentation.
 */
function isFirefoxReaderModeProbe(message: string | null | undefined): boolean {
  if (typeof message !== 'string') {
    return false
  }
  return (
    message.includes("Can't find variable: __firefox__") ||
    message.includes("window.__firefox__.reader")
  )
}

/**
 * Brave on iOS has emitted this wallet bootstrap error from the document root.
 * Keep the complete-context predicate aligned with `@aient/otel-browser` so
 * both Aient self-monitoring paths ignore the same browser-owned failure.
 */
type BrowserErrorSource = {
  filename: string
  lineno: number
  pageUrl: string
  userAgent: string
}

function isDocumentSource(filename: string, pageUrl: string): boolean {
  try {
    const source = new URL(filename, pageUrl)
    const page = new URL(pageUrl)
    return source.origin === page.origin && source.pathname === page.pathname && source.search === page.search
  } catch {
    return false
  }
}

function isBraveIosWalletProviderBootstrapError(
  message: string | null | undefined,
  source: BrowserErrorSource,
): boolean {
  return (
    typeof message === 'string' &&
    message.trim() ===
      "undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')" &&
    source.lineno === 1 &&
    source.userAgent.includes('iPhone') &&
    /\bBrave\b/.test(source.userAgent) &&
    isDocumentSource(source.filename, source.pageUrl)
  )
}

export function ErrorListeners() {
  useEffect(() => {
    function onError(event: ErrorEvent) {
      const message = event.error instanceof Error ? event.error.message : event.message
      if (isAutomationInjectedHydrationMismatch(message)) {
        return
      }
      if (isFirefoxReaderModeProbe(message)) {
        return
      }

      if (
        isBraveIosWalletProviderBootstrapError(message, {
          filename: event.filename,
          lineno: event.lineno,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        })
      ) {
        return
      }

      log.error("Unhandled error", event.error || new Error(event.message), {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        errorType: 'error',
      })
    }
    function onUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason
      const error = reason instanceof Error ? reason : new Error(String(reason))
      if (isFirefoxReaderModeProbe(error.message)) {
        return
      }
      log.error("Unhandled promise rejection", error, {
        reason: String(reason),
        errorType: 'unhandledrejection',
      })
    }

    // React emits hydration warnings via console.error (not window.onerror).
    // Suppress the same automation-injected mismatch there so otel-browser
    // doesn't forward it to observability as a real error. This intentionally
    // wraps console.error via globalThis.console.
    const browserConsole = globalThis.console
    const originalConsoleError = browserConsole.error
    browserConsole.error = (...args: unknown[]) => {
      const message = args[0]?.toString() ?? ''
      if (isAutomationInjectedHydrationMismatch(message)) return
      originalConsoleError.apply(browserConsole, args)
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
      browserConsole.error = originalConsoleError
    }
  }, [])
  return null
}
